import { CalendarClock, RefreshCw, TrendingDown } from 'lucide-react'
import type { ReactNode } from 'react'
import { Card, FitMoneyValue, MoneyValue } from '@shared/design-system/ui'
import type { Currency } from '@shared/contracts/enums'
import type { MonetaryValue } from '@shared/lib/money/money'
import { analyticsDate } from '@shared/lib/analytics-period'
import type { SubscriptionMetrics } from '../subscription-metrics'
import {
  projectionCaption,
  projectionGrid,
  projectionHero,
  projectionHeroMeta,
  projectionHeroValue,
  projectionMetric,
  projectionMetricLabel,
  projectionMetricValue,
  projectionPanel,
  projectionSecondaryGrid,
} from './subscription-metrics.css'

export function SubscriptionMetricsSummary({
  metrics,
  currency,
  header,
  titleId,
}: {
  metrics: SubscriptionMetrics
  currency: Currency
  header: ReactNode
  titleId: string
}) {
  const data = metrics.byCurrency[currency]
  if (!data)
    return (
      <Card padding="md" className={projectionPanel} role="region" aria-labelledby={titleId}>
        {header}
        <p>Nenhuma recorrência em {currency} no período selecionado.</p>
      </Card>
    )
  const baseDate = analyticsDate(
    new Date(new Date(metrics.window.baseAt).getTime() - 1).toISOString(),
  )
  const dueThrough = analyticsDate(
    new Date(new Date(metrics.window.to).getTime() - 1).toISOString(),
  )
  const ltvMessage =
    data.ltv.reason === 'NO_ACTIVE_BASE'
      ? 'Sem base ativa'
      : data.ltv.reason === 'NO_CHURN'
        ? 'Sem churn no histórico'
        : 'Histórico insuficiente'
  const sliceCaption = (slice: typeof data.projection.paid) =>
    `${slice.charges} ${slice.charges === 1 ? 'cobrança' : 'cobranças'} · ${slice.subscriptions} ${slice.subscriptions === 1 ? 'assinatura' : 'assinaturas'}`
  return (
    <Card padding="md" className={projectionPanel} role="region" aria-labelledby={titleId}>
      {header}
      <div className={projectionHero} role="group" aria-label="Já cobrado e pago">
        <div>
          <span className={projectionMetricLabel}>Já cobrado e pago</span>
          <span className={projectionHeroValue}>
            <MoneyValue {...data.projection.paid.amount} size="lg" />
          </span>
          <span className={projectionHeroMeta}>
            {data.projection.paid.charges}{' '}
            {data.projection.paid.charges === 1 ? 'pagamento confirmado' : 'pagamentos confirmados'}{' '}
            para {data.projection.paid.subscriptions}{' '}
            {data.projection.paid.subscriptions === 1 ? 'assinatura' : 'assinaturas'}
          </span>
        </div>
        <span className={projectionCaption}>
          Valor bruto das cobranças confirmadas no período das vendas vinculadas a você. Antes de
          taxas e divisões; não é o saldo disponível para saque. Uma compra pode pagar mais de uma
          assinatura.
        </span>
      </div>

      <div className={projectionGrid}>
        <Metric
          label="Ainda podem ser cobradas"
          text={`Até ${data.projection.remaining.subscriptions} ${data.projection.remaining.subscriptions === 1 ? 'assinatura' : 'assinaturas'}`}
          caption={`Vencimentos até ${dueThrough} · ${data.projection.remaining.charges} ${data.projection.remaining.charges === 1 ? 'cobrança' : 'cobranças'}`}
        />
        <Metric
          icon={<CalendarClock size={16} />}
          label="Agendado e previsto"
          value={data.projection.scheduled.amount}
          caption={`${sliceCaption(data.projection.scheduled)} · inclui cobranças em processamento`}
        />
        <Metric
          icon={<RefreshCw size={16} />}
          label="Em recuperação"
          value={data.projection.recovery.amount}
          caption={sliceCaption(data.projection.recovery)}
        />
      </div>
      <p className={projectionCaption}>
        Previsão para vencimentos no período, até {dueThrough}: até{' '}
        <MoneyValue {...data.projection.remaining.amount} size="sm" />. Inclui agendado, recuperação
        e ciclos futuros estimados; pagamentos não são garantidos.
      </p>

      <div className={projectionSecondaryGrid}>
        <Metric
          label="Assinaturas ativas"
          text={data.activeSubscriptions.toLocaleString('pt-BR')}
          caption={`Base pagante em ${baseDate}${data.overdueSubscriptions !== null ? ` · ${data.overdueSubscriptions} em atraso hoje` : ''}`}
        />
        <Metric
          label="MRR contratado"
          value={data.mrr}
          caption={`Valor bruto mensal da base em ${baseDate}`}
        />
        <Metric
          label="LTV estimado"
          value={data.ltv.value ?? undefined}
          text={data.ltv.value ? undefined : ltvMessage}
          caption={`Estimativa bruta por assinatura · ${data.ltv.historyMonths} meses completos com base; mínimo de 3 e churn positivo`}
        />
        <Metric
          icon={<TrendingDown size={16} />}
          label="Churn do período"
          text={
            data.churn.pct === null
              ? 'Sem base inicial'
              : `${data.churn.pct.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`
          }
          caption={`${data.churn.count} saídas de ${data.churn.base} assinaturas da base inicial · ${data.churn.cancellations} cancelamentos ao todo`}
        />
      </div>
      {data.projection.previousRecovery.charges > 0 && (
        <p className={projectionCaption}>
          Fora do período: {data.projection.previousRecovery.charges} cobranças anteriores ainda em
          recuperação, somando <MoneyValue {...data.projection.previousRecovery.amount} size="sm" />
          .
        </p>
      )}
      <p className={projectionCaption}>
        Pagamentos seguem a data de confirmação. Agendado e recuperação mostram o estado de hoje dos
        vencimentos selecionados. Base e MRR usam o fim do período, limitado a hoje. LTV considera
        até seis meses completos anteriores à data da base.
      </p>
    </Card>
  )
}

function Metric({
  icon,
  label,
  value,
  text,
  caption,
}: {
  icon?: ReactNode
  label: string
  value?: MonetaryValue
  text?: string
  caption: string
}) {
  return (
    <div className={projectionMetric}>
      <span className={projectionMetricLabel}>
        {icon} {label}
      </span>
      <div className={projectionMetricValue}>
        {value ? (
          <FitMoneyValue
            amount={value.amount}
            currency={value.currency}
            maxSize={16}
            minSize={12}
            fractionSize="md"
          />
        ) : (
          text
        )}
      </div>
      <span className={projectionCaption}>{caption}</span>
    </div>
  )
}
